import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import LocalStorage from '../utils/localStorage'
import authService from '../services/authService'
import type { UserProfile } from '../types/user.types'

// ============================================================
//  Auth Context
//  Global authentication state management
// ============================================================

interface AuthContextType {
    isAuthenticated: boolean
    userId: string | null
    token: string | null
    user: UserProfile | null
    userLoading: boolean
    authLoading: boolean
    login: (token: string, refreshToken: string, userId: string) => void
    logout: () => void
    checkAuth: () => boolean
    fetchUserProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
    const [userId, setUserId] = useState<string | null>(null)
    const [token, setToken] = useState<string | null>(null)
    const [user, setUser] = useState<UserProfile | null>(null)
    const [userLoading, setUserLoading] = useState<boolean>(false)
    const [authLoading, setAuthLoading] = useState<boolean>(true)

    const fetchUserProfile = useCallback(async (userIdToFetch?: string) => {
        const id = userIdToFetch || userId
        if (!id) return

        setUserLoading(true)
        try {
            const profile = await authService.getUserById(id)
            setUser(profile)
            LocalStorage.setUserData(profile)
        } catch (error) {
            console.error('Error fetching user profile:', error)
        } finally {
            setUserLoading(false)
        }
    }, [userId])

    // Check authentication on mount
    useEffect(() => {
        const authData = LocalStorage.getAuthData()
        if (authData.token && authData.userId) {
            setIsAuthenticated(true)
            setUserId(authData.userId)
            setToken(authData.token)
            // Fetch user profile
            fetchUserProfile(authData.userId)
        }
        setAuthLoading(false)
    }, [fetchUserProfile])

    const login = (accessToken: string, refreshToken: string, userIdValue: string) => {
        LocalStorage.setApiKey(accessToken)
        LocalStorage.setRefreshKey(refreshToken)
        LocalStorage.setUserId(userIdValue)
        
        setIsAuthenticated(true)
        setUserId(userIdValue)
        setToken(accessToken)
        
        // Fetch user profile after login
        fetchUserProfile(userIdValue)
    }

    const logout = () => {
        LocalStorage.clearAuth()
        
        setIsAuthenticated(false)
        setUserId(null)
        setToken(null)
        setUser(null)
        setAuthLoading(false)
    }

    const checkAuth = (): boolean => {
        const isAuth = LocalStorage.isAuthenticated()
        setIsAuthenticated(isAuth)
        return isAuth
    }

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                userId,
                token,
                user,
                userLoading,
                authLoading,
                login,
                logout,
                checkAuth,
                fetchUserProfile,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

// Custom hook to use auth context
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
